import { AttachmentSection } from '@components/Attachments'
import Modal from '@components/Modal'
import { usePatchFlightGroup } from '@hooks'
import { GetFlightGroupResponse } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import {
	AddLinkButton,
	Button,
	ButtonGroup,
	FormDescription,
	FormGroup,
	FormLabel,
	FormTextArea,
	LinkInput,
	LinkRow,
	LinksSection,
	RemoveLinkButton,
} from '../../Accommodations/AddAccommodationModal/AddAccommodationModal.styles'
import { backgroundOrange } from '@/theme/colors'

const MAX_LINKS = 3

interface EditFlightGroupModalProps {
	isOpen: boolean
	onClose: () => void
	onDelete: () => void
	group: GetFlightGroupResponse
}

const EditFlightGroupModal: React.FC<EditFlightGroupModalProps> = ({ isOpen, onClose, onDelete, group }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const patchFlightGroup = usePatchFlightGroup()

	const [notes, setNotes] = useState('')
	const [links, setLinks] = useState<string[]>([])

	useEffect(() => {
		if (isOpen && group) {
			setNotes(group.notes ?? '')
			setLinks(group.links ?? [])
		}
	}, [isOpen, group])

	const addLink = () => {
		if (links.length < MAX_LINKS) setLinks(prev => [...prev, ''])
	}
	const updateLink = (index: number, value: string) => {
		setLinks(prev => prev.map((l, i) => (i === index ? value : l)))
	}
	const removeLink = (index: number) => {
		setLinks(prev => prev.filter((_, i) => i !== index))
	}

	const canSubmit = !patchFlightGroup.isPending

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!tripId || !canSubmit) return
		const filteredLinks = links.map(l => l.trim()).filter(l => l.length > 0)
		try {
			await patchFlightGroup.mutateAsync({
				tripId,
				flightGroupId: group.id,
				data: {
					addFlightIds: [],
					removeFlightIds: [],
					notes: notes.trim() ? notes.trim() : null,
					links: filteredLinks,
				},
			})
			toast.success('Flight updated!')
			onClose()
		} catch {
			toast.error('Unable to update flight. Please try again.')
		}
	}

	const formId = 'edit-flight-group-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Flight"
			size="large"
			backgroundColor={backgroundOrange}
			closeOnBackdropClick={false}
			onDelete={onDelete}
			footer={
				<ButtonGroup>
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" form={formId} variant="primary" disabled={!canSubmit}>
						{patchFlightGroup.isPending ? 'Saving…' : 'Save Changes'}
					</Button>
				</ButtonGroup>
			}
		>
			<form id={formId} onSubmit={handleSubmit}>
				<FormGroup>
					<FormLabel>Links</FormLabel>
					<FormDescription>Add booking confirmations or relevant URLs (max {MAX_LINKS})</FormDescription>
					<LinksSection>
						{links.map((link, index) => (
							<LinkRow key={index}>
								<LinkInput
									type="text"
									value={link}
									onChange={e => updateLink(index, e.target.value)}
									placeholder="e.g. https://airline.com/booking/..."
								/>
								<RemoveLinkButton type="button" onClick={() => removeLink(index)}>
									✕
								</RemoveLinkButton>
							</LinkRow>
						))}
						<AddLinkButton type="button" onClick={addLink} disabled={links.length >= MAX_LINKS}>
							+ Add Link
						</AddLinkButton>
					</LinksSection>
				</FormGroup>

				<FormGroup>
					<FormLabel htmlFor="edit-flight-notes">Notes</FormLabel>
					<FormDescription>Confirmation number, seat assignments, baggage info, etc.</FormDescription>
					<FormTextArea
						id="edit-flight-notes"
						value={notes}
						onChange={e => setNotes(e.target.value)}
						placeholder="e.g. Confirmation ABC123, seat 14A…"
					/>
				</FormGroup>

				{tripId && (
					<AttachmentSection
						tripId={tripId}
						resourceType="FLIGHT_GROUP"
						resourceId={group.id}
						fileIds={group.fileIds || []}
					/>
				)}
			</form>
		</Modal>
	)
}

export default EditFlightGroupModal
